import { useCallback, useEffect, useRef, useState } from "react";
import { readConfig } from "../lib/config";
import { ApiError } from "../lib/http/errors";
import type { FieldApi } from "../lib/api/fieldApi";
import {
  acceptBenchReturn,
  isBenchUrl,
  returnUrlHere,
  savePendingReturn,
} from "../lib/bench/handoff";
import type { BenchReturn } from "../lib/bench/handoff";

/** 시험이 바꿔 끼우는 창 조작(열기) — 기본은 window */
export interface HandoffWindow {
  open(url: string, target: string): Window | null;
  addEventListener(type: "message", h: (ev: MessageEvent) => void): void;
  removeEventListener(type: "message", h: (ev: MessageEvent) => void): void;
}

/**
 * 'Bench 에서 고치기' — 새 탭으로 Bench 편집기를 열고(1회용 티켓), 돌아오는 postMessage 를 기다린다(lib/bench/handoff).
 *
 * 새 탭은 `window.open(url, "_blank")` 로 연다 — **noopener 를 주지 않는다**: Bench 가 저장한 뒤 `window.opener.postMessage` 로
 * 이 탭을 깨워야 하기 때문이다. 대가: Bench 탭이 `opener` 로 이 탭을 다른 주소로 보낼 수 있다(역방향 탭내빙). 그래서 ① editorUrl 이
 * 설정된 Bench 출처일 때만 열고 ② 받는 메시지는 그 출처 · 연 그 창 · 종류 · 판으로 거르며 ③ 값은 저장본 해시뿐이라 고정 올리기(서버 재검증)로만 쓴다.
 * 팝업 차단을 피하려고 클릭 순간에 빈 탭을 먼저 열고(같은 출처 about:blank), 입장권이 오면 그 탭을 Bench 로 보낸다. 실패하면 닫는다.
 *
 * 돌아온 저장본은 곧바로 쓰지 않고 localStorage 에 맡긴다(savePendingReturn) — 가져오기가 지금 안 되는 상태(차례 중 · 잠김)여도
 * 잃지 않게. onReturn 은 맡긴 뒤 부른다.
 */
export function useBenchHandoff(
  field: FieldApi | null,
  onReturn: (r: BenchReturn) => void,
  win: HandoffWindow = window,
  benchOrigin: string | null = readConfig().benchOrigin,
) {
  const opened = useRef<Window | null>(null);
  const lastProject = useRef<string | null>(null);
  const cb = useRef(onReturn);
  cb.current = onReturn;
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!benchOrigin) return;
    const h = (ev: MessageEvent) => {
      const r = acceptBenchReturn(ev, benchOrigin, opened.current ?? undefined);
      if (!r) return;
      const full = { ...r, projectId: r.projectId ?? lastProject.current };
      savePendingReturn(full);
      cb.current(full);
    };
    win.addEventListener("message", h);
    return () => win.removeEventListener("message", h);
  }, [benchOrigin, win]);

  /**
   * 클릭 핸들러에서 곧바로 부른다. projectId 는 값 또는 (빈 탭을 연 뒤에) 고르는 비동기 함수.
   * @returns 열었으면 true · Bench 가 설정되지 않았거나 고를 프로젝트가 없으면 false · 제어 평면 오류는 ApiError 로 던진다
   */
  const open = useCallback(
    async (projectId: string | null | (() => Promise<string | null>)): Promise<boolean> => {
      if (!field || !benchOrigin || busy) return false;
      const tab = win.open("", "_blank");
      setBusy(true);
      try {
        const id = typeof projectId === "function" ? await projectId() : projectId;
        if (!id) {
          tab?.close();
          return false;
        }
        const { editorUrl } = await field.benchHandoff(id, returnUrlHere());
        if (!isBenchUrl(editorUrl, benchOrigin))
          throw new ApiError("NET-SERVER", 200, "editorUrl outside bench origin");
        let w = tab;
        if (w && !w.closed) w.location.href = editorUrl;
        else w = win.open(editorUrl, "_blank");
        opened.current = w;
        lastProject.current = id;
        return true;
      } catch (e) {
        tab?.close();
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [field, benchOrigin, busy, win],
  );

  return { open, busy, available: !!benchOrigin && !!field };
}
